import Component from "./component.js";

/**
 * Sound manager component for levels.
 * Makes it easy to track sounds after they're initiated.
 * @class SoundManager
 * @param {object} config - Composite parameter object
 */
const SoundManager = class extends Component {
    constructor( config ) {
        config = config || {};
        config.name = "SoundManager";

        super( config );

        /**
         * List of currently playing effects
         * @type {array}
         * @private
         */
        this._effects = [];

        /**
         * Singular music track
         * @type {Phaser.Sound.BaseSound}
         * @private
         */
        this._music = null;

        /**
         * Whether to mute sound effects
         * @type {boolean}
         */
        this.muteEffects = false;

        /**
         * Whether to mute the music track
         * @type {boolean}
         */
        this.muteMusic = false;

        /**
         * Volume tracker, as WebAudio volume is asynchronous under Phaser.
         * @type {number}
        */
        this._volume = 1;

        // Prevent updates.
        this.active = false;
    }

    /**
     * Global volume in this level
     */
    get volume() {
        return this._volume;
    }
    set volume( value ) {
        if ( isNaN( value ) ) {
            console.error( "Volume was not a number!" );
            return;
        }

        // Do not allow values below 0. The WebAudio API allows this,
        // but it is just the same as positive volume with inverted waveforms.
        value = Math.max( 0, value );

        this._volume = value;
        this.owner.sound.setVolume( this._volume );
    }

    /**
     * Create and play an audio clip. This is largely "fire and forget":
     * the clip will play once and then remove itself.
     * @param {string|number} id - Audio key
     */
    playEffect( id ) {
        if ( !( id && this.owner.game.cache.audio.get( id ) ) ) {
            return;
        }
        const effect = this.owner.sound.add(
            id,
            { mute: this.muteEffects }
        );
        if ( effect ) {
            this._effects.push( effect );
            effect.once( "complete", () => {
                this.stopEffect( effect );
            } );
            effect.play();
        }
    }
    
    /**
     * Set the music track to the specified key, and start playing it.
     * This will override any previous music track.
     * @param {string|number} id - Audio key
     */
    playMusic( id ) {
        this.stopMusic();

        if ( !( id && this.owner.game.cache.audio.get( id ) ) ) {
            return;
        }
        
        this._music = this.owner.sound.add(
            id,
            { loop: true, mute: this.muteMusic }
        );
        if ( this._music ) {
            this._music.play();
        }
    }

    /**
     * Set the mute status of all current sound effects.
     * @param {boolean} value - whether to mute
     */
    setMuteEffects( value ) {
        this.muteEffects = !!value;
        this._effects.forEach( effect => {
            effect.mute = this.muteEffects;
        } );
    }

    /**
     * Set the mute status of the music track.
     * @param {boolean} value - whether to mute
     */
    setMuteMusic( value ) {
        if ( this._music ) {
            this._music.mute = !!value;
        }

        // Note: We track mute independent of the sound clip,
        // so we can reinstate it on other music tracks.
        this.muteMusic = !!value;
    }

    /**
     * Set the pause status of the music track.
     * @param {boolean} value - whether to pause
     */
    setPauseMusic( value ) {
        if ( this._music ) {
            if ( value ) {
                this._music.pause();
            } else {
                this._music.resume();
            }
        }
    }

    /**
     * Stop all sounds and music playing back.
     */
    stopAll() {
        this.stopMusic();
        this._effects.slice().forEach( effect => {
            this.stopEffect( effect );
        } );
    }

    /**
     * Stop an effect and destroy it.
     * @param {Phaser.Sound.BaseSound} effect - sound to terminate
     */
    stopEffect( effect ) {
        effect.destroy();
        let index = this._effects.indexOf( effect );
        if ( index > -1 ) {
            // Remove effect from tracking list.
            this._effects.splice( index, 1 );
        }
    }

    /**
     * Stop the music track and destroy it.
     */
    stopMusic() {
        if ( this._music ) {
            this._music.stop();
            this._music.destroy();
            this._music = null;
        }
    }
}

export default SoundManager;
